import { intl } from '@fullstory/intl';
import { Timestamp, int64 } from '@fullstory/proto/google/protobuf';
import { fullstorySelectors } from '@fullstory/redux';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useGetNamedElementDetailsView } from './useGetNamedElementDetailView';
import { VolumeTimeRange } from '@fullstory/proto/fs/services/dx-objects';
import { getUnionDepsForSelectors, isPendingSuggestion } from '../utils';
import { getAdjustedEndDate } from '@fullstory/util/date';
import { getVolumePoints } from '@fullstory/dqm-ui/utils';
import { useMetricsTrendData } from './useMetricsTrendData';
import { NamedElementRecord } from '@fullstory/proto/fs/services/search-settings';
import { useAiSuggestionsExploreEnabled } from '@fullstory/library-ui';

export const useNamedElementActivityData = (
  namedElementId?: string,
  suggElementRecord?: NamedElementRecord,
) => {
  const showDataDirect = useSelector(fullstorySelectors.getIsDataDirect);
  const aiSuggestionsExploreEnabled = useAiSuggestionsExploreEnabled();
  const shouldComputeSuggestedElementActivity =
    aiSuggestionsExploreEnabled && isPendingSuggestion(suggElementRecord?.namedElement);

  const { data, loading } = useGetNamedElementDetailsView({
    namedElementId,
    timeRange: showDataDirect ? VolumeTimeRange.DAY_7 : VolumeTimeRange.DAY_30,
  });

  const unionDepsForSelectors = useMemo(
    () => getUnionDepsForSelectors(suggElementRecord?.namedElement?.platformSelectors),
    [suggElementRecord?.namedElement?.platformSelectors],
  );

  const suggEvent = useMemo(() => {
    if (!shouldComputeSuggestedElementActivity) {
      return null;
    }

    return {
      primary: null,
      deps: unionDepsForSelectors,
    };
  }, [shouldComputeSuggestedElementActivity, unionDepsForSelectors]);

  const timeRange = useMemo(() => {
    if (showDataDirect) {
      return {
        startTime: 'NOW/DAY-6DAY',
        endTime: 'NOW/DAY+1DAY',
      };
    }

    return {
      startTime: 'NOW/DAY-29DAY',
      endTime: 'NOW/DAY+1DAY',
    };
  }, [showDataDirect]);

  const { trendPoints } = useMetricsTrendData(suggEvent, timeRange);

  const activityMonitoringData = useMemo(() => {
    if (namedElementId && !loading && data?.activityMonitoring) {
      return {
        totalEventCount: int64.toNumber(data.activityMonitoring.totalEventCount),
        volume: getVolumePoints(data.activityMonitoring.volume),
        firstSeen: Timestamp.toDate(data.activityMonitoring.firstSeen),
        lastSeen: Timestamp.toDate(data.activityMonitoring.lastSeen),
        firstSeenLabel: intl.formatMessage({ defaultMessage: 'First seen', id: 'tJZ3DG' }),
      };
    }

    if (suggEvent && trendPoints) {
      return {
        totalEventCount: trendPoints.reduce((sum, curr) => sum + curr.value, 0),
        volume: trendPoints,
        firstSeen: suggElementRecord?.metadata?.created
          ? Timestamp.toDate(suggElementRecord.metadata.created)
          : null,
        lastSeen:
          trendPoints.reduce<Date>((acc, curr) => {
            const adjustedEndDate = getAdjustedEndDate(curr.endDate).toDate();
            if (curr.value > 0 && adjustedEndDate > acc) {
              return adjustedEndDate;
            }
            return acc;
          }, new Date(0)) ?? null,
        firstSeenLabel: intl.formatMessage({ defaultMessage: 'Suggested on', id: 'GJo5Kx' }),
      };
    }

    return null;
  }, [
    namedElementId,
    loading,
    data?.activityMonitoring,
    suggEvent,
    trendPoints,
    suggElementRecord?.metadata?.created,
  ]);

  return {
    namedElement: namedElementId ? data?.details?.namedElement : suggElementRecord,
    activityMonitoringData,
    loading,
  };
};
